import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Utils
import { getMonthDropdown } from 'app/shared/utils/dateUtils';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import ButtonGroup from '@zg-rentals/multibrand-ui/lib/ButtonGroup';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

import { Link as v4Link } from 'react-router-dom';

import HeadWrapper from 'app/shared/components/HeadWrapper';
import AllSpendTable from 'app/shared/components/AllSpendReport/AllSpendTable';
import BoostAdTable from 'app/shared/components/BoostAdReport/BoostAdTable';

// Actions
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import ModalActions from 'app/shared/actions/ModalActions';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import adSpendUtils from 'app/shared/utils/adSpendUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import QueryConstants from 'app/shared/constants/QueryConstants';
import sharedConfig from 'app/shared/sharedConfig';

// Selectors
import { AppSelectors, AnalyticsSelectors } from 'app/shared/selectors';

import pathsManifest from 'app/shared/pathsManifest';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Ad Spend';
const MONTH_DROPDOWN = getMonthDropdown();
const ANALYTICS_CATEGORIES = {
    ALL: 'All Spend Page',
    BOOST: 'Boost Spend Page',
};

const TABS = {
    ALL: 'ALL',
    BOOST: 'BOOST',
};

const propTypes = {
    coreProductInvoice: PropTypes.object.isRequired,
    boostAdReport: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
};

class AdSpendPage extends React.Component {
    constructor(props) {
        super(props);

        const queryParams = queryUtils.parse(props.location.search);
        const insertionOrderId = queryParams[QueryConstants.AD];

        this.state = {
            isLoading: true,
            insertionOrderId,
            activeTab: insertionOrderId !== undefined ? TABS.BOOST : TABS.ALL,
            startDate: MONTH_DROPDOWN[0].value,
            hasFetchedDataOnce: false,
        };
        if (insertionOrderId !== undefined) {
            this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LOAD_BOOST_AD));
        }

        this.handlePropertyClick = this.handlePropertyClick.bind(this);
        this.handleAdClick = this.handleAdClick.bind(this);
        this.handleDownloadClick = this.handleDownloadClick.bind(this);
        this.handleMonthChange = this.handleMonthChange.bind(this);
        this.handleAllSpendClick = this.handleAllSpendClick.bind(this);
        this.handleBoostClick = this.handleBoostClick.bind(this);
        this.getData = this.getData.bind(this);
    }

    componentDidMount() {
        const { insertionOrderId, activeTab } = this.state;
        if (insertionOrderId) {
            this.props.dispatch(ModalActions.showModal({
                modalType: ModalConstants.BOOST_AD_PREVIEWS,
                insertionOrderId,
                useModalDialog: false,
            }));
        }
        this.getData(activeTab);
    }

    handleAdClick(insertionOrderId) {
        this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.VIEW_BOOST_AD));
        this.setState({ insertionOrderId });
        this.props.dispatch(ModalActions.showModal({
            modalType: ModalConstants.BOOST_AD_PREVIEWS,
            insertionOrderId,
            useModalDialog: false,
        }));
    }

    handlePropertyClick(listingKey) {
        const { dispatch } = this.props;

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.PROPERTY_COLUMN_CLICK, {
                category: ANALYTICS_CATEGORIES[this.state.activeTab],
            })
        );

        const paths = pathsManifest.getAllPaths();
        const url = `${paths.propertiesPage}/${listingKey}/${paths.propertyDetailsListing}`;
        window.router.transitionTo(url);
    }

    handleDownloadClick() {
        const { dispatch } = this.props;

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DOWNLOAD_DATA_CLICK, {
                category: ANALYTICS_CATEGORIES[this.state.activeTab],
            })
        );

        const { startDate } = this.state;
        const paths = pathsManifest.getAllPaths();
        const startDateParam = startDate ? `?startDateYyyyMmDd=${startDate}` : '';
        const excelPath = this.state.activeTab === TABS.ALL ?
            paths.exportExcel.adSpendAll :
            paths.exportExcel.boostAd;
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${startDateParam}`;
        window.open(excelUrl);
    }

    handleMonthChange(e) {
        e.preventDefault();
        const startDate = e.target.value;
        this.getData(this.state.activeTab, startDate);
    }

    handleAllSpendClick() {
        if (this.state.activeTab === TABS.ALL) {
            return;
        }
        this.props.dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.AD_SPEND_SWITCHER_CLICK, {
                category: ANALYTICS_CATEGORIES[TABS.ALL],
            })
        );
        this.setState({ activeTab: TABS.ALL });
        this.getData(TABS.ALL);
    }

    handleBoostClick() {
        if (this.state.activeTab === TABS.BOOST) {
            return;
        }
        this.props.dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.AD_SPEND_SWITCHER_CLICK, {
                category: ANALYTICS_CATEGORIES[TABS.BOOST],
            })
        );
        this.setState({ activeTab: TABS.BOOST });
        this.getData(TABS.BOOST);
    }

    getData(dataTab, startDate = this.state.startDate) {
        const params = { startDateYyyyMmDd: startDate };
        const fetchData = dataTab === TABS.ALL ?
            AnalyticsActions.getCoreProductInvoice :
            AnalyticsActions.getBoostAdReport;

        this.setState({
            isLoading: true,
            startDate,
        });

        this.props.dispatch(fetchData(params))
            .then((result) => {
                const data = result.payload;

                if (data.success === false) {
                    this.setState({
                        isLoading: false,
                        error: 'Sorry, something went wrong. Please try again later.',
                    });
                } else {
                    this.setState({
                        isLoading: false,
                        hasFetchedDataOnce: true,
                    });
                }
            })
            .catch((err) => {
                global.TEMPORARY_global_logger.error(err);
            });
    }

    renderTotal() {
        const {
            coreProductInvoice, boostAdReport, isScreenMobile, isScreenXS,
        } = this.props;
        const { isLoading, activeTab } = this.state;

        if (isLoading) {
            return null;
        }
        const totals = activeTab === TABS.ALL ?
            adSpendUtils.getCoreProductInvoiceTotals(coreProductInvoice, isScreenMobile) :
            adSpendUtils.getBoostAdTotals(boostAdReport, isScreenMobile);

        return (
            <Row>
                {isScreenXS && (
                    <Col xs={5} className="AdSpendPage-col">
                        {this.renderLink()}
                    </Col>
                )}
                <Col xs={9} sm={12} className="AdSpendPage-col AdSpendPage-align-right">
                    <Text type="h4">{totals.join(' | ')}</Text>
                </Col>
            </Row>
        );
    }

    renderTable() {
        const { isLoading, error } = this.state;
        const {
            coreProductInvoice,
            boostAdReport,
            isScreenMobile,
        } = this.props;
        if (this.state.activeTab === TABS.ALL) {
            return (
                <AllSpendTable
                    coreProductInvoiceModel={coreProductInvoice}
                    isScreenMobile={isScreenMobile}
                    onPropertyClick={this.handlePropertyClick}
                    isLoading={isLoading}
                    errorMessage={error}
                />
            );
        }
        return (
            <BoostAdTable
                boostAdReport={boostAdReport}
                isScreenMobile={isScreenMobile}
                onPropertyClick={this.handlePropertyClick}
                onAdClick={this.handleAdClick}
                isLoading={isLoading}
                errorMessage={error}
            />
        );
    }

    renderLink() {
        const { isLoading } = this.state;
        if (isLoading) {
            return null;
        }

        return (
            <Link
                reactRouterLink={v4Link}
                onClick={this.handleDownloadClick}
            >
                Export details
            </Link>
        );
    }

    renderSwitcher() {
        const { activeTab } = this.state;
        return (
            <ButtonGroup full={true}>
                <Button
                    full={true}
                    onClick={this.handleAllSpendClick}
                    type={activeTab === TABS.ALL ? 'primary' : 'outline'}
                >
                    All spend
                </Button>
                <Button
                    full={true}
                    onClick={this.handleBoostClick}
                    type={activeTab === TABS.BOOST ? 'primary' : 'outline'}
                >
                    Boost
                </Button>
            </ButtonGroup>
        );
    }

    renderDropDown() {
        const { startDate, isLoading } = this.state;
        return (
            <Select
                name="startDate"
                onChange={this.handleMonthChange}
                disabled={isLoading}
                value={startDate}
            >
                {MONTH_DROPDOWN.map(({ value, name }) => (
                    <option key={value} value={value}>
                        {name}
                    </option>
                ))}
            </Select>
        );
    }

    renderTopPage() {
        const { coreProductInvoice } = this.props;
        const showSwitcher = coreProductInvoice && coreProductInvoice.pastBoostAvailable;
        return showSwitcher ? this.renderSwitcherTopPage() : this.renderNoSwitcherTopPage();
    }

    renderNoSwitcherTopPage() {
        const { isScreenXS } = this.props;

        return (
            <div className="AdSpendPage-top-row">
                <Row>
                    <Col sm={3} className="AdSpendPage-col">
                        {this.renderDropDown()}
                    </Col>
                    {!isScreenXS && (
                        <Col sm={9} className="AdSpendPage-col AdSpendPage-align-right">
                            {this.renderLink()}
                        </Col>
                    )}
                </Row>
                {this.renderTotal()}
            </div>
        );
    }

    renderSwitcherTopPage() {
        const { isScreenXS } = this.props;

        if (isScreenXS) {
            return (
                <div className="AdSpendPage-top-row">
                    <Row>
                        <Col className="AdSpendPage-col">
                            {this.renderSwitcher()}
                        </Col>
                    </Row>
                    <Row>
                        <Col className="AdSpendPage-col">
                            {this.renderDropDown()}
                        </Col>
                    </Row>
                    {this.renderTotal()}
                </div>
            );
        }

        return (
            <div className="AdSpendPage-top-row">
                <Row>
                    <Col sm={5} lg={4} xl={3} className="AdSpendPage-col">
                        {this.renderSwitcher()}
                    </Col>
                    <Col sm={7} lg={8} xl={9} className="AdSpendPage-col AdSpendPage-align-right">
                        {this.renderLink()}
                    </Col>
                </Row>
                <SpacerRow />
                <Row>
                    <Col sm={5} lg={4} xl={3} className="AdSpendPage-col">
                        {this.renderDropDown()}
                    </Col>
                    <Col sm={7} lg={8} xl={9}>
                        {this.renderTotal()}
                    </Col>
                </Row>
            </div>
        );
    }

    render() {
        const { hasFetchedDataOnce } = this.state;
        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="AdSpendPage">
                    <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                    {hasFetchedDataOnce && this.renderTopPage()}
                    {this.renderTable()}
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    coreProductInvoice: AnalyticsSelectors.makeCoreProductInvoice(state),
    boostAdReport: AnalyticsSelectors.makeBoostAdReport(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    isScreenXS: AppSelectors.makeIsScreenXS(state),
});

AdSpendPage.propTypes = propTypes;

export { AdSpendPage };
export default connect(mapStateToProps)(AdSpendPage);
